import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

const BannerList = ({ banners, onEdit, onUpdate }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDeleteClick = (banner) => {
    setSelectedBanner(banner);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBanner) return;

    setDeleteLoading(true);

    try {
      const response = await fetch('https://api.elnaf.fi/api/delete_banner.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          banner_id: parseInt(selectedBanner.banner_id)
        })
      });

      const responseText = await response.text();
      console.log('Delete response text:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON:', e);
        alert('Invalid response from server');
        setDeleteLoading(false);
        return;
      }
      
      console.log('Delete response data:', data);
      
      // Check for success - backend might return different messages
      if (response.ok && (data.status === 'success' || data.message?.includes('uğurla') || data.message?.includes('silindi'))) {
        // Success - refresh the list
        setTimeout(() => {
          onUpdate();
          setDeleteDialogOpen(false);
          setSelectedBanner(null);
          setDeleteLoading(false);
        }, 500);
      } else {
        alert(data.message || 'Failed to delete banner');
        setDeleteLoading(false);
      }
    } catch (error) {
      console.error('Error deleting banner:', error);
      alert('Failed to delete banner');
      setDeleteLoading(false);
    }
  };

  const handlePreviewClick = (imageUrl) => {
    setPreviewImage(imageUrl);
    setPreviewDialogOpen(true);
  };

  const sortedBanners = [...banners].sort((a, b) => a.rank - b.rank);

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Rank</TableCell>
              <TableCell>Images</TableCell>
              <TableCell>Alt Texts</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedBanners.map((banner) => (
              <TableRow key={banner.banner_id} hover>
                <TableCell>
                  <Typography variant="h6">{banner.rank}</Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {banner.image_en && (
                      <Chip
                        label="EN"
                        size="small"
                        onClick={() => handlePreviewClick(banner.image_en)}
                        icon={<VisibilityIcon />}
                      />
                    )}
                    {banner.image_fin && (
                      <Chip
                        label="FIN"
                        size="small"
                        onClick={() => handlePreviewClick(banner.image_fin)}
                        icon={<VisibilityIcon />}
                      />
                    )}
                    {banner.image_fr && (
                      <Chip
                        label="FR"
                        size="small"
                        onClick={() => handlePreviewClick(banner.image_fr)}
                        icon={<VisibilityIcon />}
                      />
                    )}
                    {banner.image_de && (
                      <Chip
                        label="DE"
                        size="small"
                        onClick={() => handlePreviewClick(banner.image_de)}
                        icon={<VisibilityIcon />}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="caption" display="block">
                      <strong>EN:</strong> {banner.alt_text_en || '-'}
                    </Typography>
                    <Typography variant="caption" display="block">
                      <strong>FIN:</strong> {banner.alt_text_fin || '-'}
                    </Typography>
                    <Typography variant="caption" display="block">
                      <strong>FR:</strong> {banner.alt_text_fr || '-'}
                    </Typography>
                    <Typography variant="caption" display="block">
                      <strong>DE:</strong> {banner.alt_text_de || '-'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={banner.status === '1' ? 'Active' : 'Inactive'}
                    color={banner.status === '1' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => onEdit(banner)}
                    title="Edit"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(banner)}
                    title="Delete"
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Banner</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this banner? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading && <CircularProgress size={20} color="inherit" />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Banner Preview</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" py={2}>
            <img
              src={previewImage}
              alt="Banner preview"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BannerList;